import { TableScroll } from '@web/components/table-scroll.tsx';
import { useReportPaging } from '@web/components/report-pagination.tsx';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { reportsApi } from '@web/api/endpoints.ts';
import { EmptyState, classes } from '@web/components/ui.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { formatMinutes } from '@web/i18n/format.ts';
import { getDateLocale } from '@web/i18n/locale.ts';
import {
  reportRetry,
  StatTile,
  ReportBoundary,
  SectionCard,
  TH,
  THR,
  TD,
  TDR,
  ROW,
  type RangeParams,
} from '../shared.tsx';

export function CrewSection({ range, scope }: { range: RangeParams; scope?: 'all' }) {
  const { t } = useTranslation();
  const paging = useReportPaging({ ...range, scope });
  const pages = paging.pages;
  const query = useQuery({
    queryKey: ['reports', 'crew', { ...range, scope, pages }, paging.identity],
    queryFn: () => reportsApi.crew({ ...range, scope, pages }),
    retry: reportRetry,
  });

  return (
    <ReportBoundary query={query} paging={paging}>
      {(report) => {
        const money = (cents: number) => formatCents(cents, report.currencyCode, getDateLocale());
        // Minutes and revenue sum cleanly over the member rows (labor-minutes
        // and an even split, respectively); visits must NOT — a three-person
        // job appears on three rows — so the distinct count comes from the
        // report's own totals.
        const totals = report.members.reduce(
          (sum, row) => ({
            visits: sum.visits,
            minutes: sum.minutes + row.actualMinutes,
            revenue: sum.revenue + row.attributedRevenueCents,
          }),
          { visits: report.totals.completedVisits, minutes: 0, revenue: 0 },
        );

        if (report.members.length === 0 && (report.pagination?.members?.page ?? 1) === 1) {
          return <EmptyState title={t('reports.crew.emptyTitle')}>{t('reports.crew.emptyBody')}</EmptyState>;
        }

        return (
          <>
            <div className="mb-6 grid gap-4 sm:grid-cols-3">
              <StatTile label={t('reports.crew.completedVisits')} value={String(totals.visits)} />
              <StatTile
                label={t('reports.crew.timeWorked')}
                value={formatMinutes(report.totals.actualMinutes ?? totals.minutes)}
              />
              <StatTile
                label={t('reports.crew.revenue')}
                value={money(report.totals.attributedRevenueCents ?? totals.revenue)}
                hint={t('reports.crew.revenueHint')}
              />
            </div>

            <SectionCard
              title={t('reports.crew.tableTitle')}
              collection="members"
              hint={t('reports.crew.tableHint')}
              csvHref={reportsApi.csvUrl('crew', { ...range, scope })}
            >
              <TableScroll aria-label={t('reports.crew.tableTitle')} className="overflow-x-auto">
                <table className="data-table w-full min-w-max border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-[color:var(--color-line)]">
                      <th scope="col" className={TH}>
                        {t('reports.crew.colMember')}
                      </th>
                      <th scope="col" className={THR}>
                        {t('reports.crew.colVisits')}
                      </th>
                      <th scope="col" className={THR}>
                        {t('reports.crew.colScheduled')}
                      </th>
                      <th scope="col" className={THR}>
                        {t('reports.crew.colWorked')}
                      </th>
                      <th scope="col" className={THR}>
                        {t('reports.crew.colRevenue')}
                      </th>
                      <th scope="col" className={THR}>
                        {t('reports.crew.colCanceled')}
                      </th>
                      <th scope="col" className={THR}>
                        {t('reports.crew.colNoShows')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.members.map((row) => (
                      <tr key={row.userId ?? 'unassigned'} className={ROW}>
                        <td className={classes(TD, 'table-text max-w-[18rem]')}>
                          {row.userId === null ? (
                            <span className="text-[color:var(--color-muted)]">{t('reports.crew.unassigned')}</span>
                          ) : (
                            row.name
                          )}
                        </td>
                        <td className={TDR}>{row.completedVisits}</td>
                        <td className={TDR}>{formatMinutes(row.scheduledMinutes)}</td>
                        <td className={TDR}>{formatMinutes(row.actualMinutes)}</td>
                        <td className={TDR}>{money(row.attributedRevenueCents)}</td>
                        <td className={TDR}>{row.cancelledVisits}</td>
                        <td className={TDR}>{row.noShowVisits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            </SectionCard>
          </>
        );
      }}
    </ReportBoundary>
  );
}
